import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  ArrowLeft, 
  Package, 
  CheckCircle, 
  Clock,
  Building2,
  Home,
  User,
  Calendar,
  FileText,
  Eye,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  PackagePlus,
  PackageMinus,
  AlertTriangle,
  History,
  ArrowDown,
  Filter as FilterIcon,
  Settings
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DetalleSolicitudModal from '@/components/inventario/DetalleSolicitudModal';
import AlistarEntregaModal from '@/components/inventario/AlistarEntregaModal';
import VerComprobanteModal from '@/components/conductor/VerComprobanteModal';
import DeclineModal from '@/components/inventario/DeclineModal';
import { fetchSolicitudesPendientes, fetchEntregas, declinarSolicitud } from '@/services/entregasInsumos.service';
import { fetchInventarioInsumos } from './services/inventario-insumos.services';
import { fetchMovimientosInsumos } from '@/services/movimientosInsumos.service';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;

const EntregaInsumosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Estados
  const [activeTab, setActiveTab] = useState('pendientes');
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [entregasAlistadas, setEntregasAlistadas] = useState([]);
  const [entregasEntregadas, setEntregasEntregadas] = useState([]);
  
  const [isLoadingSolicitudes, setIsLoadingSolicitudes] = useState(false);
  const [isLoadingAlistadas, setIsLoadingAlistadas] = useState(false);
  const [isLoadingEntregadas, setIsLoadingEntregadas] = useState(false);

  const [currentPageSolicitudes, setCurrentPageSolicitudes] = useState(1);
  const [currentPageAlistadas, setCurrentPageAlistadas] = useState(1);
  const [currentPageEntregadas, setCurrentPageEntregadas] = useState(1);

  const [totalSolicitudes, setTotalSolicitudes] = useState(0);
  const [totalAlistadas, setTotalAlistadas] = useState(0);
  const [totalEntregadas, setTotalEntregadas] = useState(0);

  // Estados para Inventario
  const [inventario, setInventario] = useState([]);
  const [isLoadingInventario, setIsLoadingInventario] = useState(false);
  const [currentPageInventario, setCurrentPageInventario] = useState(1);
  const [totalItemsInventario, setTotalItemsInventario] = useState(0);

  // Estados para Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [currentPageMovimientos, setCurrentPageMovimientos] = useState(1);
  const [totalItemsMovimientos, setTotalItemsMovimientos] = useState(0);

  // Estados para Historial
  const [historialDetallado, setHistorialDetallado] = useState([]);
  const [isLoadingHistorial, setIsLoadingHistorial] = useState(false);

  // Estados para Filtros
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});

  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalInsumos: 0,
    totalCantidad: 0,
    bajoStock: 0,
    agotados: 0
  });

  // Estados para modales
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  const [isAlistarModalOpen, setIsAlistarModalOpen] = useState(false);
  const [isComprobanteModalOpen, setIsComprobanteModalOpen] = useState(false);
  const [solicitudComprobante, setSolicitudComprobante] = useState(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  // Handlers (definidos antes de las columnas)
  const handleVerDetalle = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDetalleModalOpen(true);
  };

  const handleAlistar = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsAlistarModalOpen(true);
  };

  const handleVerComprobante = (entrega) => {
    // Transformar la entrega al formato esperado por el modal
    const solicitudFormateada = {
      ...entrega,
      tipo: 'insumos',
      displayName: entrega.edificio?.nombre || 'Sin edificio',
      solicitante: entrega.solicitante?.nombre || 'Desconocido',
      fechaSolicitud: entrega.createdAt,
      items: entrega.items || []
    };
    setSolicitudComprobante(solicitudFormateada);
    setIsComprobanteModalOpen(true);
  };

  const handleAlistamientoSuccess = () => {
    loadSolicitudesPendientes();
    loadEntregasAlistadas();
    
    toast({
      title: 'Éxito',
      description: 'La entrega ha sido alistada correctamente',
    });
  };

  const handleEntregar = (entrega) => {
    console.log('Entregar:', entrega);
    toast({
      title: 'Funcionalidad en desarrollo',
      description: 'El modal de entrega se implementará próximamente'
    });
  };

  const handleDeclinar = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDeclineModalOpen(true);
  };

  const handleConfirmDeclinar = async (justificacion) => {
    setIsDeclining(true);
    try {
      const usuario = JSON.parse(localStorage.getItem('user') || '{}');
      
      await declinarSolicitud({
        solicitud_id: selectedSolicitud._id,
        usuario: {
          id: usuario._id || usuario.id,
          nombre: usuario.nombre
        },
        justificacion
      });

      toast({
        title: 'Éxito',
        description: 'La solicitud ha sido declinada correctamente',
      });

      setIsDeclineModalOpen(false);
      setSelectedSolicitud(null);
      loadSolicitudesPendientes();
    } catch (error) {
      console.error('Error al declinar solicitud:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo declinar la solicitud',
        variant: 'destructive'
      });
    } finally {
      setIsDeclining(false);
    }
  };

  // Columnas para solicitudes pendientes
  const columnsSolicitudes = [
    {
      id: 'fecha',
      label: 'Fecha Solicitud',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString('es-ES')}
          </span>
        </div>
      )
    },
    {
      id: 'edificio',
      label: 'Edificio',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.edificio?.nombre}</div>
            <div className="text-xs text-gray-500">{row.edificio?.ciudad}</div>
          </div>
        </div>
      )
    },
    {
      id: 'hospedaje',
      label: 'Activo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-green-600" />
          <div>
            <div className="font-medium">{row.hospedaje?.nombre || row.nombrePropiedad}</div>
            <div className="text-xs text-gray-500 capitalize">{row.hospedaje?.tipo}</div>
          </div>
        </div>
      )
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-600" />
          <span className="text-sm">{row.creator?.nombre}</span>
        </div>
      )
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-orange-600" />
          <Badge variant="outline" className="font-semibold">
            {row.total_solicitado} items
          </Badge>
        </div>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => (
        <Badge className="bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          {row.estado}
        </Badge>
      )
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50'
        },
        {
          key: 'alistar',
          icon: 'Package',
          tooltip: 'Alistar',
          className: 'text-green-600 hover:bg-green-50'
        },
        {
          key: 'declinar',
          icon: 'X',
          tooltip: 'Declinar',
          className: 'text-red-600 hover:bg-red-50'
        }
      ]
    }
  ];

  // Función para manejar acciones de la tabla de solicitudes
  const handleActionSolicitudes = (actionKey, row) => {
    if (actionKey === 'ver-detalle') {
      handleVerDetalle(row);
    } else if (actionKey === 'alistar') {
      handleAlistar(row);
    } else if (actionKey === 'declinar') {
      handleDeclinar(row);
    }
  };

  // Columnas para entregas alistadas y entregadas
  const columnsEntregas = [
    {
      id: 'fecha',
      label: 'Fecha',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <div>
            <div className="text-sm font-medium">
              {new Date(row.fecha_alistamiento || row.createdAt).toLocaleDateString('es-ES')}
            </div>
            {row.fecha_entrega && (
              <div className="text-xs text-gray-500">
                Entregado: {new Date(row.fecha_entrega).toLocaleDateString('es-ES')}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'edificio',
      label: 'Edificio',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-600" />
          <div>
            <div className="font-medium">{row.edificio?.nombre}</div>
            <div className="text-xs text-gray-500">{row.edificio?.ciudad}</div>
          </div>
        </div>
      )
    },
    {
      id: 'hospedaje',
      label: 'Activo',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-green-600" />
          <span className="font-medium">{row.hospedaje?.nombre}</span>
        </div>
      )
    },
    {
      id: 'alistador',
      label: 'Alistador',
      render: (row) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-purple-600" />
          <span className="text-sm">{row.alistador?.nombre || '-'}</span>
        </div>
      )
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <Badge variant="outline" className="font-semibold">
          <Package className="h-3 w-3 mr-1" />
          {row.total_items} items
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      render: (row) => {
        const colors = {
          'alistado': 'bg-blue-100 text-blue-800',
          'entregado': 'bg-green-100 text-green-800',
          'cancelado': 'bg-red-100 text-red-800'
        };
        const icons = {
          'alistado': <Package className="h-3 w-3 mr-1" />,
          'entregado': <CheckCircle className="h-3 w-3 mr-1" />,
          'cancelado': <FileText className="h-3 w-3 mr-1" />
        };
        return (
          <Badge className={colors[row.estado]}>
            {icons[row.estado]}
            {row.estado}
          </Badge>
        );
      }
    }
  ];

  // Columnas para entregas alistadas (con botón de entregar)
  const columnsAlistadas = [
    ...columnsEntregas,
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'entregar',
          icon: 'CheckCircle',
          tooltip: 'Entregar',
          className: 'text-green-600 hover:bg-green-50'
        }
      ]
    }
  ];

  // Función para manejar acciones de entregas alistadas
  const handleActionAlistadas = (actionKey, row) => {
    if (actionKey === 'entregar') {
      handleEntregar(row);
    }
  };

  // Función para manejar acciones de entregas entregadas
  const handleActionEntregadas = (actionKey, row) => {
    if (actionKey === 'ver-comprobante') {
      handleVerComprobante(row);
    }
  };

  // Columnas para entregas entregadas (con botón de ver comprobante)
  const columnsEntregadas = [
    ...columnsEntregas.filter(col => col.id !== 'actions' && col.id !== 'acciones'),
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-comprobante',
          icon: 'FileText',
          tooltip: 'Ver Comprobante',
          className: 'text-green-600 hover:bg-green-50',
        },
      ],
    }
  ];

  // Columnas para Inventario Actual
  const columnsInventario = [
    {
      id: 'consumible',
      label: 'Insumo',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-green-600" />
          <span className="font-medium">{row.consumible?.nombre || row.consumible || '-'}</span>
        </div>
      )
    },
    {
      id: 'cantidad_disponible',
      label: 'Cantidad Disponible',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-green-50 text-green-700 font-semibold">
          {row.cantidad_disponible}
        </Badge>
      )
    },
    {
      id: 'cantidad_minima',
      label: 'Cantidad Mínima',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-600">{row.cantidad_minima}</span>
      )
    },
    {
      id: 'ubicacion',
      label: 'Ubicación',
      render: (row) => row.ubicacion || '-'
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'disponible': 'bg-green-100 text-green-800',
          'bajo_stock': 'bg-yellow-100 text-yellow-800',
          'agotado': 'bg-red-100 text-red-800',
          'inactivo': 'bg-gray-100 text-gray-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'ultima_entrada',
      label: 'Última Entrada',
      render: (row) => row.ultima_entrada 
        ? new Date(row.ultima_entrada).toLocaleDateString('es-ES')
        : '-'
    }
  ];

  // Columnas para Movimientos
  const columnsMovimientos = [
    {
      id: 'fecha_movimiento',
      label: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.fecha_movimiento).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo',
      sortable: true,
      render: (row) => {
        const colors = {
          'entrada': 'bg-green-100 text-green-800',
          'salida': 'bg-red-100 text-red-800',
          'traslado': 'bg-blue-100 text-blue-800',
          'ajuste': 'bg-yellow-100 text-yellow-800',
          'baja': 'bg-gray-100 text-gray-800'
        };
        const icons = {
          'entrada': <TrendingUp className="h-3 w-3" />,
          'salida': <TrendingDown className="h-3 w-3" />,
          'traslado': <Package className="h-3 w-3" />,
          'ajuste': <AlertCircle className="h-3 w-3" />,
          'baja': <FileText className="h-3 w-3" />
        };
        return (
          <Badge className={`${colors[row.tipo_movimiento]} flex items-center gap-1 w-fit`}>
            {icons[row.tipo_movimiento]}
            {row.tipo_movimiento}
          </Badge>
        );
      }
    },
    {
      id: 'consumible',
      label: 'Insumo',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-green-600" />
          <span>{row.consumible?.nombre || '-'}</span>
        </div>
      )
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      sortable: true,
      render: (row) => {
        const cantidad = row.cantidad;
        const isNegative = cantidad < 0;
        return (
          <Badge variant="outline" className="font-semibold">
            {isNegative ? '' : '+'}{cantidad}
          </Badge>
        );
      }
    },
    {
      id: 'numero_factura',
      label: 'Factura/Consecutivo',
      render: (row) => row.numero_factura || row.consecutivo || '-'
    },
    {
      id: 'usuario',
      label: 'Usuario',
      render: (row) => row.usuario?.nombre || 'N/A'
    },
    {
      id: 'cantidad_despues',
      label: 'Stock Resultante',
      render: (row) => (
        <span className="text-sm font-medium text-gray-700">
          {row.cantidad_despues !== undefined ? row.cantidad_despues : '-'}
        </span>
      )
    }
  ];

  // Handlers para filtros
  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  // Cargar solicitudes pendientes
  const loadSolicitudesPendientes = useCallback(async () => {
    setIsLoadingSolicitudes(true);
    try {
      const response = await fetchSolicitudesPendientes({
        page: currentPageSolicitudes,
        limit: ITEMS_PER_PAGE
      });

      if (response && response.solicitudes) {
        setSolicitudesPendientes(response.solicitudes);
        setTotalSolicitudes(response.total || 0);
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes pendientes',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingSolicitudes(false);
    }
  }, [currentPageSolicitudes, toast]);

  // Cargar entregas alistadas
  const loadEntregasAlistadas = useCallback(async () => {
    setIsLoadingAlistadas(true);
    try {
      const response = await fetchEntregas({
        page: currentPageAlistadas,
        limit: ITEMS_PER_PAGE,
        estado: 'alistado'
      });

      if (response && response.data) {
        setEntregasAlistadas(response.data);
        setTotalAlistadas(response.total || 0);
      }
    } catch (error) {
      console.error('Error al cargar entregas alistadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas alistadas',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingAlistadas(false);
    }
  }, [currentPageAlistadas, toast]);

  // Cargar entregas entregadas
  const loadEntregasEntregadas = useCallback(async () => {
    setIsLoadingEntregadas(true);
    try {
      const response = await fetchEntregas({
        page: currentPageEntregadas,
        limit: ITEMS_PER_PAGE,
        estado: 'entregado'
      });

      if (response && response.data) {
        setEntregasEntregadas(response.data);
        setTotalEntregadas(response.total || 0);
      }
    } catch (error) {
      console.error('Error al cargar entregas entregadas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas entregadas',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingEntregadas(false);
    }
  }, [currentPageEntregadas, toast]);

  // Cargar Inventario Actual
  const loadInventario = useCallback(async () => {
    setIsLoadingInventario(true);
    try {
      const offset = (currentPageInventario - 1) * ITEMS_PER_PAGE;
      const response = await fetchInventarioInsumos({
        page: currentPageInventario,
        limit: ITEMS_PER_PAGE,
        offset,
        query: filters
      });

      setInventario(response.data || []);
      setTotalItemsInventario(response.totalRecords || 0);

      // Calcular estadísticas
      const totalCantidad = response.data?.reduce((sum, item) => sum + item.cantidad_disponible, 0) || 0;
      const bajoStock = response.data?.filter(item => item.estado === 'bajo_stock').length || 0;
      const agotados = response.data?.filter(item => item.estado === 'agotado').length || 0;

      setStats({
        totalInsumos: response.totalRecords || 0,
        totalCantidad,
        bajoStock,
        agotados
      });
    } catch (error) {
      console.error('Error al cargar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingInventario(false);
    }
  }, [currentPageInventario, filters, toast]);

  // Cargar Movimientos
  const loadMovimientos = useCallback(async () => {
    setIsLoadingMovimientos(true);
    try {
      const offset = (currentPageMovimientos - 1) * ITEMS_PER_PAGE;
      const response = await fetchMovimientosInsumos({
        page: currentPageMovimientos,
        limit: ITEMS_PER_PAGE,
        offset,
        ...filters
      });

      if (response && response.movimientos) {
        setMovimientos(response.movimientos);
        setTotalItemsMovimientos(response.total || 0);
      } else {
        setMovimientos([]);
        setTotalItemsMovimientos(0);
      }
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar los movimientos',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingMovimientos(false);
    }
  }, [currentPageMovimientos, filters, toast]);

  // Cargar Historial Detallado
  const loadHistorialDetallado = useCallback(async () => {
    setIsLoadingHistorial(true);
    try {
      const params = {
        limit: 1000,
        estadoExcluir: 'pendiente'
      };
      
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
      
      // Obtener inventario completo
      const inventarioResponse = await fetchInventarioInsumos({
        limit: 10000,
        offset: 0,
        query: {}
      });
      
      if (inventarioResponse.data) {
        setInventario(inventarioResponse.data);
      }
      
      // Obtener entregas
      const response = await fetchEntregas(params);
      const data = response.data || [];
      
      const filteredData = data.filter(item => item.estado === 'entregado');
      
      const sortedData = filteredData.sort((a, b) => {
        const dateA = new Date(a.fecha_entrega || a.createdAt);
        const dateB = new Date(b.fecha_entrega || b.createdAt);
        return dateA - dateB;
      });
      
      setHistorialDetallado(sortedData);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial detallado',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistorial(false);
    }
  }, [filters, toast]);

  // Construir matriz de entregas
  const buildStockMatrix = () => {
    if (!historialDetallado || historialDetallado.length === 0) {
      return { supplies: [], events: [] };
    }

    const suppliesMap = new Map();
    
    historialDetallado.forEach(entrega => {
      if (entrega.items && Array.isArray(entrega.items)) {
        entrega.items.forEach(item => {
          if (!suppliesMap.has(item.id)) {
            const inventarioItem = inventario.find(inv => inv.consumible?.id === item.id);
            
            suppliesMap.set(item.id, {
              id: item.id,
              nombre: item.nombre,
              tipo: item.tipo || 'General',
              inventarioActual: inventarioItem?.cantidad_disponible || 0
            });
          }
        });
      }
    });

    const events = historialDetallado.map(entrega => {
      const itemsMap = new Map();
      
      if (entrega.items && Array.isArray(entrega.items)) {
        entrega.items.forEach(item => {
          itemsMap.set(item.id, {
            cantidad: item.cantidad,
            impacto: -item.cantidad
          });
        });
      }

      const edificioNombre = entrega.edificio?.nombre || 'N/A';
      const hospedajeNombre = entrega.hospedaje?.nombre || null;
      const destino = hospedajeNombre ? `${edificioNombre} - ${hospedajeNombre}` : edificioNombre;

      return {
        id: entrega._id,
        consecutivo: entrega.solicitud_id || entrega._id,
        fecha: new Date(entrega.fecha_entrega || entrega.createdAt),
        edificio: edificioNombre,
        hospedaje: hospedajeNombre,
        destino: destino,
        itemsMap
      };
    });

    const supplies = Array.from(suppliesMap.values()).sort((a, b) => 
      a.nombre.localeCompare(b.nombre)
    );

    return { supplies, events };
  };

  // Effects
  useEffect(() => {
    if (activeTab === 'pendientes') {
      loadSolicitudesPendientes();
    } else if (activeTab === 'alistadas') {
      loadEntregasAlistadas();
    } else if (activeTab === 'entregadas') {
      loadEntregasEntregadas();
    } else if (activeTab === 'inventario') {
      loadInventario();
    } else if (activeTab === 'movimientos') {
      loadMovimientos();
    } else if (activeTab === 'historial') {
      loadHistorialDetallado();
    }
  }, [activeTab, loadSolicitudesPendientes, loadEntregasAlistadas, loadEntregasEntregadas, loadInventario, loadMovimientos, loadHistorialDetallado]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/lavanderia')}
              className="h-10 w-10 shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-4 flex-1">
              <div className="bg-green-50 p-3 rounded-lg shrink-0">
                <ShoppingCart className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Entrega de Insumos</CardTitle>
                <CardDescription>
                  Gestionar alistamiento y entrega de insumos
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Statistics Cards */}
      {activeTab === 'inventario' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Insumos</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.totalInsumos}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Cantidad Total</p>
                  <p className="text-2xl font-bold text-green-600">{stats.totalCantidad}</p>
                </div>
                <PackagePlus className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bajo Stock</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.bajoStock}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Agotados</p>
                  <p className="text-2xl font-bold text-red-600">{stats.agotados}</p>
                </div>
                <PackageMinus className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-6 mb-6 bg-gray-100 p-1 rounded-lg h-auto gap-1">
              <TabsTrigger 
                value="pendientes" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-yellow-700 data-[state=active]:font-semibold data-[state=active]:border-yellow-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Solicitudes</span>
                {totalSolicitudes > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700 text-xs">
                    {totalSolicitudes}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="alistadas" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700 data-[state=active]:font-semibold data-[state=active]:border-blue-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Package className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Alistadas</span>
                {totalAlistadas > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 text-xs">
                    {totalAlistadas}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="entregadas" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-green-700 data-[state=active]:font-semibold data-[state=active]:border-green-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <CheckCircle className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Entregadas</span>
                {totalEntregadas > 0 && (
                  <Badge variant="secondary" className="ml-1 data-[state=active]:bg-green-100 data-[state=active]:text-green-700 text-xs">
                    {totalEntregadas}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="inventario" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-purple-700 data-[state=active]:font-semibold data-[state=active]:border-purple-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <Package className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Inventario</span>
              </TabsTrigger>
              <TabsTrigger 
                value="movimientos" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-orange-700 data-[state=active]:font-semibold data-[state=active]:border-orange-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Movimientos</span>
              </TabsTrigger>
              <TabsTrigger 
                value="historial" 
                className="flex items-center justify-center gap-2 py-3 px-2 rounded-md transition-all border border-gray-200 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-indigo-700 data-[state=active]:font-semibold data-[state=active]:border-indigo-200 hover:bg-gray-50 hover:border-gray-300"
              >
                <History className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Historial</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab: Solicitudes Pendientes */}
            <TabsContent value="pendientes" className="space-y-4">
              <DataTable
                columns={columnsSolicitudes}
                data={solicitudesPendientes}
                isLoading={isLoadingSolicitudes}
                onAction={handleActionSolicitudes}
              />
              <Pagination
                currentPage={currentPageSolicitudes}
                totalItems={totalSolicitudes}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageSolicitudes}
              />
            </TabsContent>

            {/* Tab: Entregas Alistadas */}
            <TabsContent value="alistadas" className="space-y-4">
              <DataTable
                columns={columnsAlistadas}
                data={entregasAlistadas}
                isLoading={isLoadingAlistadas}
                onAction={handleActionAlistadas}
              />
              <Pagination
                currentPage={currentPageAlistadas}
                totalItems={totalAlistadas}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageAlistadas}
              />
            </TabsContent>

            {/* Tab: Entregas Entregadas */}
            <TabsContent value="entregadas" className="space-y-4">
              <DataTable
                columns={columnsEntregadas}
                data={entregasEntregadas}
                isLoading={isLoadingEntregadas}
                onAction={handleActionEntregadas}
              />
              <Pagination
                currentPage={currentPageEntregadas}
                totalItems={totalEntregadas}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageEntregadas}
              />
            </TabsContent>

            {/* Tab: Inventario Actual */}
            <TabsContent value="inventario" className="space-y-4">
              <DataTable
                columns={columnsInventario}
                data={inventario}
                isLoading={isLoadingInventario}
              />
              <Pagination
                currentPage={currentPageInventario}
                totalItems={totalItemsInventario}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageInventario}
              />
            </TabsContent>

            {/* Tab: Movimientos */}
            <TabsContent value="movimientos" className="space-y-4">
              <DataTable
                columns={columnsMovimientos}
                data={movimientos}
                isLoading={isLoadingMovimientos}
              />
              <Pagination
                currentPage={currentPageMovimientos}
                totalItems={totalItemsMovimientos}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageMovimientos}
              />
            </TabsContent>

            {/* Tab: Historial Detallado */}
            <TabsContent value="historial" className="space-y-4">
              {isLoadingHistorial ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Cargando historial...</p>
                  </div>
                </div>
              ) : historialDetallado.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No hay registros en el historial</p>
                  </CardContent>
                </Card>
              ) : (() => {
                const { supplies, events } = buildStockMatrix();
                
                return (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="sticky top-0 bg-gray-50 z-10">
                            <tr>
                              <th className="sticky left-0 bg-gray-100 border border-gray-300 p-3 text-left font-semibold text-sm min-w-[200px] z-20">
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-green-600" />
                                  Insumo
                                </div>
                              </th>
                              {events.map((event) => (
                                <th 
                                  key={event.id}
                                  className="border border-gray-300 p-2 text-center font-semibold text-xs min-w-[120px] bg-orange-50"
                                >
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-center gap-1">
                                      <ArrowDown className="h-3 w-3 text-orange-600" />
                                      <span className="text-orange-700">ENT</span>
                                    </div>
                                    <div className="text-xs font-mono text-gray-600">
                                      {event.consecutivo.substring(0, 15)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {event.fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                                    </div>
                                    <div className="text-xs text-gray-600 font-medium">
                                      {event.destino}
                                    </div>
                                  </div>
                                </th>
                              ))}
                              <th className="sticky right-0 bg-blue-100 border border-gray-300 p-3 text-center font-semibold text-sm min-w-[100px] z-20">
                                Total
                              </th>
                              <th className="sticky right-0 bg-green-100 border border-gray-300 p-3 text-center font-semibold text-sm min-w-[120px] z-20">
                                Inventario Actual
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplies.map((supply) => {
                              let totalEntregado = 0;
                              
                              return (
                                <tr key={supply.id} className="hover:bg-gray-50">
                                  <td className="sticky left-0 bg-white border border-gray-300 p-3 font-medium text-sm z-10">
                                    <div className="flex items-center gap-2">
                                      <Package className="h-4 w-4 text-green-600" />
                                      {supply.nombre}
                                    </div>
                                  </td>
                                  {events.map((event) => {
                                    const itemData = event.itemsMap.get(supply.id);
                                    const cantidad = itemData?.cantidad || 0;
                                    
                                    if (cantidad > 0) totalEntregado += cantidad;
                                    
                                    return (
                                      <td 
                                        key={event.id}
                                        className={`border border-gray-300 p-2 text-center text-sm ${
                                          cantidad > 0 ? 'bg-orange-50 font-semibold text-orange-700' : 'bg-white text-gray-400'
                                        }`}
                                      >
                                        {cantidad > 0 ? cantidad : '-'}
                                      </td>
                                    );
                                  })}
                                  <td className="sticky right-0 bg-blue-50 border border-gray-300 p-3 text-center font-bold text-blue-700 z-10">
                                    {totalEntregado}
                                  </td>
                                  <td className="sticky right-0 bg-green-50 border border-gray-300 p-3 text-center font-bold text-green-700 z-10">
                                    {supply.inventarioActual || 0}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modales */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={() => setIsDetalleModalOpen(false)}
        solicitud={selectedSolicitud}
      />

      <AlistarEntregaModal
        isOpen={isAlistarModalOpen}
        onClose={() => setIsAlistarModalOpen(false)}
        solicitud={selectedSolicitud}
        onSuccess={handleAlistamientoSuccess}
      />

      <VerComprobanteModal
        isOpen={isComprobanteModalOpen}
        onClose={() => {
          setIsComprobanteModalOpen(false);
          setSolicitudComprobante(null);
        }}
        solicitud={solicitudComprobante}
      />

      <DeclineModal
        isOpen={isDeclineModalOpen}
        onClose={() => {
          setIsDeclineModalOpen(false);
          setSelectedSolicitud(null);
        }}
        solicitud={selectedSolicitud}
        onConfirm={handleConfirmDeclinar}
        isLoading={isDeclining}
      />
    </motion.div>
  );
};

export default EntregaInsumosPage;
